import React, { HTMLAttributes, ReactNode, memo, useEffect, useRef } from 'react';
import './style.scss';
import TextStyle from '@able/react/dist/TextStyle';
import Divider from '@able/react/dist/Divider';
import Icon from '@able/react/dist/Icon';
import Grid from '@able/react/dist/Grid';
import Column from '@able/react/dist/Column';
import { ableTextAlias } from '../util/able-migration';

type InPageNavProps = {
    links: Link[];
    developmentUrl: string;
    stickyPosition: number;
    navLabel: string;
    gap: number;
    zIndex: number;
    className?: string;
};

export type InPageNavigationProps = {
    links: Link[];
    developmentUrl: string;
    stickyPosition?: number;
    navLabel?: string;
    gap?: number;
    className?: string;
    children: ReactNode;
    zIndex?: number;
};

type Link = {
    label: string | JSX.Element[] | JSX.Element;
    id: string;
    events?: Record<string, unknown>;
};

const InPageNav = ({
    links,
    developmentUrl,
    stickyPosition,
    gap,
    navLabel,
    className,
    zIndex,
}: InPageNavProps) => {
    const [surfaceVariant, setSurfaceVariant] = React.useState('flat');
    const [section, setSection] = React.useState<string>('');
    const [showButtonLeft, setShowButtonLeft] = React.useState(false);
    const [showButtonRight, setShowButtonRight] = React.useState(false);

    const ulRef = React.useRef<HTMLUListElement>(null);
    const linkClickRef = React.useRef<boolean>(false);
    const crossPointRef = React.useRef<number>();
    const leftButtonRef = React.useRef<HTMLDivElement>(null);
    const rightButtonRef = React.useRef<HTMLDivElement>(null);

    let timer: NodeJS.Timeout;

    const scrollVisible = (link: HTMLAnchorElement) => {
        const buttonRight = rightButtonRef.current?.getBoundingClientRect().left as number;
        const buttonLeft = leftButtonRef.current?.getBoundingClientRect().right as number;
        const linkPosition = link.getBoundingClientRect();
        if (linkPosition.right + 8 > buttonRight) {
            (ulRef.current as HTMLUListElement).scrollBy(linkPosition.right - buttonRight + 16, 0);
        } else if (linkPosition.left - 8 < buttonLeft) {
            (ulRef.current as HTMLUListElement).scrollBy(linkPosition.left - buttonLeft - 16, 0);
        }
    };
    const scrollFunction = () => {
        const ul = ulRef.current as HTMLElement;
        setShowButtonLeft(ul.scrollLeft > 0);
        setShowButtonRight(ul.clientWidth + Math.ceil(ul.scrollLeft) < ul.scrollWidth);
    };

    React.useEffect(() => {
        scrollFunction();
        window.addEventListener('resize', scrollFunction);
        return () => window.removeEventListener('resize', scrollFunction);
    }, [links]);

    const scrollSectionFunction = () => {
        const navBar = document.querySelector('.tedui-in-page-nav_container') as HTMLElement;
        const navBarTop = navBar.getBoundingClientRect().top as number;
        if (navBarTop <= stickyPosition) {
            setSurfaceVariant('slight');
            navBar.style.zIndex = `${zIndex}`;
            if (rightButtonRef.current) rightButtonRef.current.style.zIndex = `${zIndex + 1}`;
            if (leftButtonRef.current) leftButtonRef.current.style.zIndex = `${zIndex + 1}`;
        } else if (navBarTop - 2 > stickyPosition) {
            setSurfaceVariant('flat');
            setSection('');
            navBar.style.zIndex = '0';
            if (rightButtonRef.current) rightButtonRef.current.style.zIndex = '1';
            if (leftButtonRef.current) leftButtonRef.current.style.zIndex = '1';
        }

        for (const link of links) {
            const element = document.getElementById(link.id) as Element;
            if (!element) {
                continue;
            }
            const position = element.getBoundingClientRect() as DOMRect;
            if (
                Math.floor(position.top) <= Math.ceil((crossPointRef.current as number) + 1) &&
                Math.floor(position.bottom) > Math.ceil((crossPointRef.current as number) + 1)
            ) {
                setSection(link.id);
                if (timer !== null) {
                    clearTimeout(timer);
                }
                timer = setTimeout(() => {
                    const active = document.querySelector(
                        `a[href='#${link.id}']`
                    ) as HTMLAnchorElement;
                    if (active) {
                        scrollVisible(active);
                    }
                }, 100);
                break;
            }
        }
    };

    const scrollToSection = (section: HTMLElement) => {
        crossPointRef.current =
            stickyPosition +
            ((
                document.querySelector('.tedui-in-page-nav_container') as Element
            ).getBoundingClientRect().height as number) +
            gap;
        section.tabIndex = -1;
        linkClickRef.current = false;
        const headerOffset = crossPointRef.current as number;
        const elementPosition = section.getBoundingClientRect().top;
        const offsetPosition = Math.ceil(elementPosition - headerOffset + window.scrollY);
        window.scrollTo({
            behavior: 'smooth',
            top: offsetPosition,
        });
        section.focus({ preventScroll: true });
    };

    React.useEffect(() => {
        const element =
            window.location.hash &&
            (document.querySelector(`${window.location.hash}`) as HTMLElement);
        if (!element) {
            return;
        }
        scrollToSection(element);
    }, []);

    React.useEffect(() => {
        crossPointRef.current =
            stickyPosition +
            ((
                document.querySelector('.tedui-in-page-nav_container') as Element
            ).getBoundingClientRect().height as number) +
            gap;
        window.addEventListener('scroll', scrollSectionFunction);
        return () => window.removeEventListener('scroll', scrollSectionFunction);
    }, [stickyPosition, gap, links, zIndex]);

    const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, link: Link) => {
        e.preventDefault();
        const element = document.querySelector(`#${link.id}`) as HTMLElement;
        if (!element) {
            return;
        }
        history.pushState(null, '', `#${link.id}`);
        scrollToSection(element);
    };

    const handleLeftButtonClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        const buttonRight = (e.target as HTMLButtonElement).getBoundingClientRect().right;
        const linkList = [...document.querySelectorAll('.tedui-in-page-nav_li')].reverse();
        for (const link of linkList) {
            const linkLeft = link.getBoundingClientRect().left;
            if (buttonRight > linkLeft) {
                (ulRef.current as HTMLUListElement).scrollBy(linkLeft - buttonRight - 16, 0);
                break;
            }
        }
    };
    const handleRightButtonClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        const buttonLeft = (e.target as HTMLButtonElement).getBoundingClientRect().left;
        const linkList = document.querySelectorAll('.tedui-in-page-nav_li');
        for (const link of linkList) {
            const linkRight = link.getBoundingClientRect().right;
            if (linkRight > buttonLeft) {
                (ulRef.current as HTMLUListElement).scrollBy(linkRight - buttonLeft + 16, 0);
                break;
            }
        }
    };

    const navClassName = `tedui-in-page-nav_container ${!className ? '' : className}`;

    return (
        <nav className={navClassName} style={{ top: stickyPosition }} aria-label={navLabel}>
            {surfaceVariant === 'flat' && <Divider />}
            <div
                className={`tedui-in-page-nav_surface tedui-in-page-nav_surface_${surfaceVariant}`}
            >
                <Grid>
                    <Column className="grid" cols={12}>
                        <div className="tedui-in-page-nav_surface_width">
                            {showButtonLeft && (
                                <div className="tedui-in-page-nav_left_button" ref={leftButtonRef}>
                                    <button
                                        aria-label="Show previous links"
                                        onClick={(e) => handleLeftButtonClick(e)}
                                        tabIndex={-1}
                                    >
                                        <Icon
                                            icon="ChevronLeft"
                                            size="32"
                                            developmentUrl={developmentUrl}
                                        />
                                    </button>
                                </div>
                            )}
                            <ul
                                className="tedui-in-page-nav_ul"
                                onScroll={scrollFunction}
                                ref={ulRef}
                            >
                                {links.map((link, index) => {
                                    return (
                                        <li className={`tedui-in-page-nav_li`} key={index}>
                                            <a
                                                className={`tedui-in-page-nav_a ${
                                                    section === link.id ? 'selected' : ''
                                                }`}
                                                href={`#${link.id}`}
                                                {...link.events}
                                                onClick={(e) => {
                                                    handleLinkClick(e, link);
                                                    const { onClick } = link.events || {};
                                                    if (onClick && typeof onClick === 'function') {
                                                        onClick();
                                                    }
                                                }}
                                                onMouseDown={() => {
                                                    linkClickRef.current = true;
                                                    const { onMouseDown } = link.events || {};
                                                    if (
                                                        onMouseDown &&
                                                        typeof onMouseDown === 'function'
                                                    ) {
                                                        onMouseDown();
                                                    }
                                                }}
                                                onFocus={(e) => {
                                                    !linkClickRef.current &&
                                                        scrollVisible(
                                                            e.target as HTMLAnchorElement
                                                        );
                                                    const { onFocus } = link.events || {};
                                                    if (onFocus && typeof onFocus === 'function') {
                                                        onFocus();
                                                    }
                                                }}
                                            >
                                                <TextStyle alias={ableTextAlias("Label")} element="span">
                                                    {link.label}
                                                </TextStyle>
                                            </a>
                                        </li>
                                    );
                                })}
                            </ul>
                            {showButtonRight && (
                                <div
                                    className="tedui-in-page-nav_right_button"
                                    ref={rightButtonRef}
                                >
                                    <button
                                        aria-label="Show next links"
                                        onClick={(e) => handleRightButtonClick(e)}
                                        tabIndex={-1}
                                    >
                                        <Icon
                                            icon="ChevronRight"
                                            size="32"
                                            developmentUrl={developmentUrl}
                                        />
                                    </button>
                                </div>
                            )}
                        </div>
                    </Column>
                </Grid>
            </div>
            {surfaceVariant === 'flat' && <Divider />}
        </nav>
    );
};

const PageNavigation = memo(InPageNav);

export default function InPageNavigation({
    links,
    developmentUrl,
    stickyPosition = 0,
    gap = 32,
    navLabel = 'In Page Navigation',
    className,
    children,
    zIndex = 1,
}: InPageNavigationProps) {
    const divRef = useRef<HTMLDivElement>(null);

    const setScrollMarginTop = () => {
        if (divRef.current) {
            divRef.current.style.cssText = `--scroll-margin-top: ${
                Math.floor(
                    (
                        document.querySelector('.tedui-in-page-nav_container') as Element
                    ).getBoundingClientRect().height as number
                ) +
                gap +
                stickyPosition -
                1
            }px`;
        }
    };
    useEffect(() => {
        const checkSections = () => {
            for (const i of links) {
                const section = document.getElementById(i.id);
                if (!section) {
                    console.error(
                        `In-page Navigation item needs a corresponding <section> element for link ${i.label}.`
                    );
                    break;
                }
            }
        };
        checkSections();
        const observer = new MutationObserver(checkSections);
        observer.observe(divRef.current as Element, { childList: true });
        return () => observer.disconnect();
    }, [links]);

    useEffect(() => {
        const query = matchMedia('(min-width: 64rem)');
        setScrollMarginTop();
        query.addEventListener('change', setScrollMarginTop);
        return () => query.removeEventListener('change', setScrollMarginTop);
    }, [gap, stickyPosition, links]);
    return (
        <div className="tedui-in-page-nav" ref={divRef}>
            <PageNavigation
                developmentUrl={developmentUrl}
                links={links}
                gap={gap}
                className={className}
                navLabel={navLabel}
                stickyPosition={stickyPosition}
                zIndex={zIndex}
            />
            {children}
        </div>
    );
}

export type INavSection = {
    id: string;
    className?: string;
    children: ReactNode;
} & HTMLAttributes<HTMLElement>;

export const NavSection = ({ id, className, children, ...other }: INavSection) => {
    return (
        <section {...other} id={id} className={`tedui-nav-section ${className || ''}`}>
            {children}
        </section>
    );
};
